import type { Direction } from "../models/ModuleGrid.js";

/** 연결 모양 정의 — 고정 포트 방향 + 표시 심볼. 회전 개념 없이 방향별 모양을 명시한다. */
export interface ShapeDef {
  readonly id: string;
  readonly displayName: string;
  readonly symbol: string;
  readonly ports: readonly Direction[];
}

/**
 * 모양 카탈로그 — 방향별 명시 모양(회전 제거). 직선 2종 / 코너 4종 / T자 4종 / 십자 1종 = 11종.
 * 각 모양은 고정 포트라 rotation이 불필요하다(과거 회전 동치류 1개 + rotatePorts 방식 폐기).
 * 효과↔모양은 1:1이 아니라 `modules.tsv`의 shapePool로 효과별 2~3개 풀에서 선택(탭=순환)한다.
 * 심볼은 박스드로잉 문자로 실제 포트 방향을 반영한다(┗=up+right 등).
 */
export const SHAPE_CATALOG: Record<string, ShapeDef> = {
  straight_h: { id: "straight_h", displayName: "가로직선", symbol: "━", ports: ["left", "right"] },
  straight_v: { id: "straight_v", displayName: "세로직선", symbol: "┃", ports: ["up", "down"] },
  corner_ru: { id: "corner_ru", displayName: "코너 우상", symbol: "┗", ports: ["up", "right"] },
  corner_rd: { id: "corner_rd", displayName: "코너 우하", symbol: "┏", ports: ["right", "down"] },
  corner_dl: { id: "corner_dl", displayName: "코너 좌하", symbol: "┓", ports: ["down", "left"] },
  corner_lu: { id: "corner_lu", displayName: "코너 좌상", symbol: "┛", ports: ["left", "up"] },
  t_up: { id: "t_up", displayName: "T자 위", symbol: "┻", ports: ["left", "right", "up"] },
  t_down: { id: "t_down", displayName: "T자 아래", symbol: "┳", ports: ["left", "right", "down"] },
  t_left: { id: "t_left", displayName: "T자 왼쪽", symbol: "┫", ports: ["up", "down", "left"] },
  t_right: { id: "t_right", displayName: "T자 오른쪽", symbol: "┣", ports: ["up", "down", "right"] },
  cross: { id: "cross", displayName: "십자", symbol: "╋", ports: ["up", "right", "down", "left"] },
};

/** 알 수 없는 shapeId의 안전 기본값(가로직선). */
export const FALLBACK_SHAPE_ID = "straight_h";

export function shapeDef(shapeId: string): ShapeDef {
  return SHAPE_CATALOG[shapeId] ?? SHAPE_CATALOG[FALLBACK_SHAPE_ID]!;
}

const portsKey = (ports: readonly Direction[]): string => [...ports].sort().join(",");

/**
 * 포트 집합 → 그 모양의 심볼(역매핑). 다중셀 배치 시 footprint 방향이 모양을 바꾸므로(straight_h를
 * vert2에 두면 세로 직선), 팔레트 미리보기 심볼을 셀의 **실제 포트**로 도출해 배치와 일치시키는 데 쓴다.
 * 일치 모양이 없으면 fallback 심볼.
 */
export function symbolForPorts(ports: readonly Direction[]): string {
  const key = portsKey(ports);
  for (const def of Object.values(SHAPE_CATALOG)) {
    if (portsKey(def.ports) === key) return def.symbol;
  }
  return shapeDef(FALLBACK_SHAPE_ID).symbol;
}

/**
 * 포트 집합 → 그 모양의 shapeId (역매핑). 일치 모양이 없으면 FALLBACK_SHAPE_ID.
 * symbolForPorts의 id 버전 — 경로 방향(이전·다음 이웃)으로 실제 배치 모양을 도출하는 데 쓴다
 * (예: {left,up}→corner_lu, {left,right}→straight_h).
 */
export function shapeIdForPorts(ports: readonly Direction[]): string {
  const key = portsKey(ports);
  for (const def of Object.values(SHAPE_CATALOG)) {
    if (portsKey(def.ports) === key) return def.id;
  }
  return FALLBACK_SHAPE_ID;
}
